import { Link } from "react-router-dom";
import {
  ArrowRight,
  Video,
  Sparkles,
  CalendarClock,
  Users,
  FolderOpen,
  Workflow,
  CreditCard,
  Brain,
  Check,
  Globe,
  Award,
  Briefcase,
  Building2,
  Rocket,
} from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";

export default function Landing() {
  return (
    <div className="bg-[#fbfcfc]">
      <MarketingNav />
      <Hero />
      <Trust />
      <ProductPreview />
      <Features />
      <WhyBloq />
      <UseCases />
      <CtaBand />
      <MarketingFooter />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="bloq-hero-orb" style={{ width: 700, height: 700, top: -200, left: "10%" }} />
      <div className="bloq-hero-orb" style={{ width: 500, height: 500, top: 100, right: "-5%", background: "radial-gradient(circle at 70% 30%, rgba(245,239,230,0.7), transparent 70%)" }} />
      <div className="bloq-container pt-16 pb-24 sm:pt-24 sm:pb-32 relative">
        <div className="max-w-3xl mx-auto text-center">
          <span className="bloq-pill bloq-fade-up">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#005e7a" }} />
            AI-native meeting workspace · v1
            <span
              className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
              style={{ background: "#b45309", color: "white" }}
              data-testid="hero-beta-badge"
            >
              Beta
            </span>
          </span>
          <h1
            className="bloq-display text-5xl sm:text-6xl lg:text-7xl mt-7 mb-7 bloq-fade-up bloq-fade-up-d1"
            data-testid="hero-headline"
          >
            Turn every meeting into <span className="bloq-gradient-text">momentum.</span>
          </h1>
          <p className="text-lg sm:text-xl text-[#4a4a52] max-w-2xl mx-auto leading-relaxed bloq-fade-up bloq-fade-up-d2">
            Host meetings, book clients, capture next steps, and keep your workflow moving
            — without duct-taping five different tools together. Bloq is the workspace your meetings deserve.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center bloq-fade-up bloq-fade-up-d3">
            <Link to="/beta-disclaimer" className="bloq-btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base" data-testid="hero-primary-cta">
              Start building your meeting workflow
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/features" className="bloq-btn-secondary inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base" data-testid="hero-secondary-cta">
              See how Bloq works
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Trust() {
  // Each ICP card now gets a leading icon — visually grounds who Bloq is
  // for at a glance, more memorable than a list of plain text pills.
  const ICPS = [
    { label: "Coaches & consultants", icon: Award },
    { label: "Solopreneurs", icon: Briefcase },
    { label: "Service businesses", icon: Building2 },
    { label: "Startup teams", icon: Rocket },
  ];
  return (
    <section className="pb-10">
      <div className="bloq-container">
        <p className="text-center bloq-eyebrow mb-6">Built for the workflow you actually run</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
          {ICPS.map(({ label, icon: Icon }) => (
            <div
              key={label}
              className="bloq-card p-5 text-center text-sm font-medium text-[#1a1a1f] flex flex-col items-center gap-2.5"
              data-testid={`icp-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ background: "rgba(0,94,122,0.1)", color: "#005e7a" }}
              >
                <Icon className="w-4 h-4" />
              </div>
              {label}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductPreview() {
  return (
    <section className="bloq-section">
      <div className="bloq-container">
        <div className="text-center mb-12">
          <p className="bloq-eyebrow">A meeting workspace, fully assembled</p>
          <h2 className="bloq-headline text-4xl sm:text-5xl mt-3 max-w-2xl mx-auto">
            One screen. The whole follow-through.
          </h2>
        </div>
        <div className="bloq-glass-strong p-2 sm:p-3 max-w-5xl mx-auto bloq-fade-up">
          <div className="rounded-[10px] overflow-hidden" style={{ background: "linear-gradient(180deg, #f9fafb 0%, #ffffff 100%)" }}>
            <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: "1px solid rgba(26,26,31,0.06)" }}>
              <div className="w-2.5 h-2.5 rounded-full bg-[#e34a4a]/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#e3b94a]/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#4ae374]/60" />
              <span className="ml-3 text-xs text-[#82828c] font-mono">bloq.video/app · Dashboard</span>
            </div>
            <div className="grid grid-cols-12 p-5 gap-5">
              <div className="col-span-12 md:col-span-8 flex flex-col gap-5">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { l: "Meetings this week", v: "12", d: "+3 vs last" },
                    { l: "Hours saved", v: "4.8h", d: "via AI follow-ups" },
                    { l: "Follow-ups", v: "7", d: "ready to send" },
                    { l: "Bookings", v: "9", d: "this month" },
                  ].map((s, i) => (
                    <div key={i} className="bloq-card p-3.5">
                      <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-medium">{s.l}</p>
                      <p className="text-2xl bloq-headline mt-1">{s.v}</p>
                      <p className="text-[11px] text-[#82828c] mt-0.5">{s.d}</p>
                    </div>
                  ))}
                </div>
                <div className="bloq-card p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold">Upcoming today</p>
                    <span className="text-xs text-[#82828c]">3 meetings</span>
                  </div>
                  <ul className="flex flex-col gap-3">
                    {[
                      { t: "Discovery — Acme Corp", time: "10:00", duration: "30m" },
                      { t: "Sprint review", time: "13:30", duration: "45m" },
                      { t: "Client retainer · Diana", time: "16:00", duration: "60m" },
                    ].map((m, i) => (
                      <li key={i} className="flex items-center justify-between text-sm py-2 px-3 rounded-lg hover:bg-[#f5efe6] transition-colors">
                        <div className="flex items-center gap-3">
                          <Video className="w-4 h-4 text-[#005e7a]" />
                          <span className="font-medium">{m.t}</span>
                        </div>
                        <span className="text-xs text-[#82828c]">{m.time} · {m.duration}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="col-span-12 md:col-span-4 flex flex-col gap-5">
                <div className="bloq-card p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-[#005e7a]" />
                    <p className="text-sm font-semibold">Last meeting · summary</p>
                  </div>
                  <p className="text-sm text-[#4a4a52] leading-relaxed">
                    Diana wants the proposal by Thursday. Move kickoff to Aug 19. Send NDA from contracts library.
                  </p>
                  <p className="text-xs mt-3 text-[#82828c]">3 action items · 2 decisions · draft ready</p>
                </div>
                <div className="bloq-card p-5">
                  <p className="text-sm font-semibold mb-3">World clocks</p>
                  <div className="flex flex-col gap-2 text-sm">
                    {[
                      { c: "Stockholm", t: "14:22" },
                      { c: "New York", t: "08:22" },
                      { c: "Tokyo", t: "21:22" },
                    ].map((c) => (
                      <div key={c.c} className="flex items-center justify-between">
                        <span className="text-[#4a4a52]">{c.c}</span>
                        <span className="font-mono">{c.t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  const items = [
    { icon: Video, t: "HD video meetings", d: "Secure rooms with lobby, screen share, chat, raise-hand, and host controls — no 40-minute cliffs." },
    { icon: CalendarClock, t: "Custom booking pages", d: "Public booking pages with multiple appointment types, intake forms, custom locations, and Google Calendar 2-way sync." },
    { icon: Sparkles, t: "AI-powered summaries", d: "Concise recaps with action items, decisions, and follow-up drafts the moment a meeting ends." },
    { icon: Workflow, t: "One-click follow-ups", d: "Send polished meeting notes, action items, and the recording to every attendee in a single click." },
    { icon: FolderOpen, t: "Cloud recording + transcripts", d: "Encrypted recordings stored in your private bucket, transcripts indexed and searchable." },
    { icon: Users, t: "Contacts CRM + client profiles", d: "Every booking auto-creates a contact with a 7-digit customer ID, history, payments, and notes." },
    { icon: CreditCard, t: "Deposits & invoicing", d: "Collect deposits or full payment at booking, then invoice the balance. Built for paid sessions." },
    { icon: Globe, t: "Team workspaces", d: "Workspace owners, hosts, co-hosts, attendees, and guest invites — permissions done right." },
    { icon: Brain, t: "Meeting intelligence", d: "Time saved, follow-up velocity, and what each conversation actually moved forward." },
  ];
  return (
    <section className="bloq-section" style={{ background: "linear-gradient(180deg, #fbfcfc 0%, #f5efe6 100%)" }}>
      <div className="bloq-container">
        <div className="max-w-2xl mb-14">
          <p className="bloq-eyebrow">Why Bloq exists</p>
          <h2 className="bloq-headline text-4xl sm:text-5xl mt-3 mb-4">
            Nine tools, replaced by one calm interface.
          </h2>
          <p className="text-[#4a4a52] text-lg leading-relaxed">
            Bloq isn't a Zoom clone. It's the workspace that wraps the meeting — before, during, after.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it, i) => {
            const I = it.icon;
            return (
              <div key={i} className="bloq-card p-7 hover:-translate-y-1 transition-transform" data-testid={`feature-${it.t.toLowerCase().replace(/ /g, "-")}`}>
                <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-5" style={{ background: "rgba(0,94,122,0.1)", color: "#005e7a" }}>
                  <I className="w-5 h-5" />
                </div>
                <p className="bloq-headline text-xl mb-2 text-[#005e7a]">{it.t}</p>
                <p className="text-sm text-[#4a4a52] leading-relaxed">{it.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyBloq() {
  return (
    <section className="bloq-section">
      <div className="bloq-container grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="bloq-eyebrow">Why Bloq</p>
          <h2 className="bloq-headline text-4xl sm:text-5xl mt-3 mb-5">
            Every conversation deserves a next step.
          </h2>
          <p className="text-[#4a4a52] text-lg leading-relaxed mb-8">
            Most meeting tools end at "leave call." Bloq begins there. We make the
            five minutes after the meeting the most important part of the meeting.
          </p>
          <ul className="flex flex-col gap-3">
            {[
              "Tool sprawl down to one calm workspace",
              "Follow-ups drafted by AI, sent by you",
              "Public booking pages clients actually trust",
              "Workspace permissions built for client work",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-[#1a1a1f]">
                <span className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0" style={{ background: "rgba(0,94,122,0.1)" }}>
                  <Check className="w-3 h-3 text-[#005e7a]" />
                </span>
                <span className="text-sm">{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bloq-glass-strong p-1 relative overflow-hidden">
          <img src="https://static.prod-images.emergentagent.com/jobs/b1e51bf8-62a4-44cb-812d-c4efe1161101/images/a63c22eca0103a18d6b79537317af478416c48612db329a6d416904eeda3276c.png" alt="Bloq glass shapes" className="rounded-[12px] w-full" />
        </div>
      </div>
    </section>
  );
}

function UseCases() {
  const cases = [
    { t: "Coaches", d: "Run discovery, deliver sessions, and capture client homework in one place." },
    { t: "Consultants", d: "Send booking links, recap meetings, and follow up like a pro — automatically." },
    { t: "Solopreneurs", d: "Less tab-juggling. More billable hours actually billed." },
    { t: "Service teams", d: "Workspace-wide meeting playbooks and shared follow-up libraries." },
  ];
  return (
    <section className="bloq-section" style={{ background: "#1a1a1f", color: "#fff" }}>
      <div className="bloq-container">
        <p className="text-[#a8c1c9] text-xs uppercase tracking-[0.18em] font-medium">Use cases</p>
        <h2 className="bloq-headline text-4xl sm:text-5xl mt-3 mb-12 max-w-2xl">
          Built for people who get paid to show up — and follow up.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cases.map((c) => (
            <div key={c.t} className="p-6 rounded-xl" style={{ background: "rgba(255,255,255,0.04)" }}>
              <p className="bloq-headline text-xl mb-3 text-white">{c.t}</p>
              <p className="text-sm text-white/65 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingPreview() {
  const tiers = [
    { name: "Free", price: "$0", desc: "For individual users getting started." },
    { name: "Lite", price: "$6", desc: "Solo consultants who book a lot." },
    { name: "Pro", price: "$12", desc: "Growing service businesses.", featured: true },
    { name: "Business", price: "$24", desc: "Teams running client work." },
    { name: "Enterprise", price: "$60", desc: "Scale, SSO, dedicated support." },
  ];
  return (
    <section className="bloq-section">
      <div className="bloq-container">
        <div className="text-center mb-12">
          <p className="bloq-eyebrow">Pricing</p>
          <h2 className="bloq-headline text-4xl sm:text-5xl mt-3 mb-3">Honest pricing. Real value.</h2>
          <p className="text-[#4a4a52] text-lg max-w-xl mx-auto">
            Start free. Upgrade when Bloq starts saving you hours every week.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {tiers.map((t) => (
            <div
              key={t.name}
              className={`p-5 rounded-xl ${t.featured ? "bloq-glass-strong" : "bloq-card"}`}
              style={t.featured ? { outline: "2px solid #005e7a" } : {}}
            >
              <p className="bloq-headline text-xl">{t.name}</p>
              <p className="bloq-display text-3xl my-2">{t.price}<span className="text-sm text-[#82828c] font-normal">/mo</span></p>
              <p className="text-xs text-[#4a4a52] leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-[#82828c]">Subject to change. Annual billing coming soon.</p>
        <div className="mt-10 text-center">
          <Link to="/pricing" className="bloq-btn-secondary inline-flex items-center gap-2">See full pricing <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="bloq-section">
      <div className="bloq-container">
        <div
          className="relative overflow-hidden p-10 sm:p-14 rounded-2xl bloq-fade-up"
          style={{ background: "linear-gradient(135deg, #005e7a 0%, #00475c 100%)", color: "#fff" }}
        >
          <div className="bloq-hero-orb" style={{ width: 500, height: 500, top: -150, right: -100, background: "radial-gradient(circle, rgba(245,239,230,0.18), transparent 70%)" }} />
          <p className="text-[#a8c1c9] text-xs uppercase tracking-[0.18em] font-medium">Ready when you are</p>
          <h2 className="bloq-display text-4xl sm:text-5xl mt-3 max-w-2xl mb-3">
            Bloq keeps the meeting from disappearing the second everyone leaves.
          </h2>
          <p className="text-white/80 text-lg max-w-xl mb-8">
            Free to start. Built for people who run their business on conversations.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/beta-disclaimer" data-testid="ctaband-signup-button" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white text-[#005e7a] font-medium hover:bg-[#f5efe6] transition-colors">
              Start your workspace
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white/10 text-white font-medium hover:bg-white/20 transition-colors">
              Talk to the team
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
